import { useState, useEffect } from 'react';
import { UseCheckoutResult, CheckoutDataWithAddresses } from '../types';
import { CheckoutService } from '../services/checkoutService';
import { AddressService } from '../services/addressService';
import { useUser } from '../contexts/UserContext';

export function useCheckout(): UseCheckoutResult {
  const [checkoutData, setCheckoutData] = useState<CheckoutDataWithAddresses | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, promoCodes } = useUser();

  useEffect(() => {
    let isMounted = true;

    const fetchCheckoutData = async () => {
      // Wait for auth state to resolve so guestPrefix() returns the correct value
      if (!window.__USER_FETCHED__) {
        await new Promise<void>((resolve) => {
          const interval = setInterval(() => {
            if (window.__USER_FETCHED__) {
              clearInterval(interval);
              resolve();
            }
          }, 50);
        });
      }

      if (!isMounted) return;

      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
        }

        // Fetch checkout data (without savedShippingLocations and promoCodes)
        const fetchedData = await CheckoutService.getCheckoutData();

        // fetchCheckoutData already waited for window.__USER_FETCHED__, which is only set
        // after the promo fetch completes. Use window.__USER_PROMO_CODES__ directly so
        // promoCodes is guaranteed fresh rather than the stale closure value from the
        // initial render when promos hadn't loaded yet.
        const checkoutWithExtras: CheckoutDataWithAddresses = {
          ...fetchedData,
          savedShippingLocations: [],
          promoCodes: window.__USER_PROMO_CODES__ ?? promoCodes,
        };

        // Fetch addresses separately if user is available
        if (user?.id) {
          try {
            const addressResponse = await AddressService.getUserAddresses(user.id);
            checkoutWithExtras.savedShippingLocations = addressResponse.data;
          } catch (addressError) {
            console.warn('Failed to fetch user addresses, using empty addresses:', addressError);
            // Keep empty array if address fetch fails
          }
        }

        if (isMounted) {
          setCheckoutData(checkoutWithExtras);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load checkout data';
        if (isMounted) {
          setError(errorMessage);
        }
        console.error('Error fetching checkout data:', err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCheckoutData();

    return () => {
      isMounted = false;
    };
  }, [user?.id, promoCodes]);

  return {
    checkoutData,
    setCheckoutData,
    loading,
    error,
  };
}
